import React from "react";
import styles from "./InTaxBaseComponent.module.css";

import InTaxHeaderBar from "./InTaxHeaderBar/InTaxHeaderBar";
import InTaxSummaryCard from "./InTaxSummaryCard/InTaxSummaryCard";
import InTaxIncomeSection from "./IncomeSection/InTaxIncomeSection/InTaxIncomeSection";

import useInTaxConfig from "./hooks/useInTaxConfig";

export default function InTaxBaseComponent({ userEmail = "" }) {
  const { loading: cfgLoading, index: cfgIndex, error: cfgError } = useInTaxConfig();

  const [year, setYear] = React.useState(new Date().getFullYear());
  const [jurisdiction, setJurisdiction] = React.useState({
    countryCode: "ca",
    provinceOrState: "ab",
    filingStatus: "single",
  });

  // demo sources (replace with data hook)
  const [sources] = React.useState([
    { id: "s1", name: "Jobber" },
    { id: "s2", name: "Freelance (Side)" },
  ]);

  const countryOptions = React.useMemo(() => {
    if (!cfgIndex) return [];
    return (cfgIndex.countries || []).map((c) => ({
      value: String(c.code || "").toLowerCase(),
      label: `${c.flag || ""} ${c.name}`.trim(),
    }));
  }, [cfgIndex]);

  const yearOptions = React.useMemo(() => {
    if (!cfgIndex) return [];
    return cfgIndex.listYearsForCountry(jurisdiction.countryCode).map((y) => y);
  }, [cfgIndex, jurisdiction.countryCode]);

  const regionOptions = React.useMemo(() => {
    if (!cfgIndex) return [];
    return cfgIndex.listRegionsForCountry(jurisdiction.countryCode);
  }, [cfgIndex, jurisdiction.countryCode]);

  React.useEffect(() => {
    if (!cfgIndex || !yearOptions.length) return;
    if (!yearOptions.includes(year)) setYear(yearOptions[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfgIndex, jurisdiction.countryCode, yearOptions.join("|")]);

  React.useEffect(() => {
    if (!cfgIndex || !regionOptions.length) return;
    if (!regionOptions.includes(jurisdiction.provinceOrState)) {
      setJurisdiction((j) => ({ ...j, provinceOrState: regionOptions[0] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfgIndex, jurisdiction.countryCode, regionOptions.join("|")]);

  const summary = React.useMemo(() => {
    return {
      ytdGross: 0,
      ytdTaxWithheld: 0,
      ytdOtherDeductions: 0,
      ytdTakeHome: 0,
      expectedYtdTax: 0,
    };
  }, []);

  return (
    <div className={styles.wrap}>
      <div className="container-fluid">
        <div className="pp-page">
          {/* Unified sticky header for page identity */}
          <div className="pp-page-header d-flex align-items-center justify-content-between">
            <h1 className="pp-page-title mb-0">Income Tax Forecast</h1>
            <div className="pp-page-actions" />
          </div>

          {/* Alerts */}
          {(cfgLoading || cfgError) && (
            <div className="pp-section">
              {cfgLoading && (
                <div className="alert alert-secondary py-2 mb-0">Loading tax config…</div>
              )}
              {cfgError && (
                <div className="alert alert-danger py-2 mb-0">
                  Failed to load tax config{cfgError?.message ? ` — ${cfgError.message}` : "."}
                </div>
              )}
            </div>
          )}

          {/* Controls / Header bar */}
          <div className="pp-section">
            <InTaxHeaderBar
              year={year}
              onYearChange={setYear}
              jurisdiction={jurisdiction}
              onJurisdictionChange={setJurisdiction}
              options={{
                countries: countryOptions,
                years: yearOptions,
                regions: regionOptions,
              }}
              configIndex={cfgIndex}
              loading={cfgLoading}
            />
          </div>

          {/* Summary */}
          <div className="pp-section">
            <InTaxSummaryCard
              year={year}
              jurisdiction={jurisdiction}
              summary={summary}
              configIndex={cfgIndex}
            />
          </div>

          {/* Income sections */}
          <div className="pp-section">
            {sources.map((src, idx) => (
              <InTaxIncomeSection
                key={src.id}
                defaultOpen={idx === 0}
                source={src}
                configIndex={cfgIndex}
                year={year}
                jurisdiction={jurisdiction}
                userEmail={userEmail}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
